import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { ActionIcon, Avatar, Menu, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import Empty from 'src/components/Feedback/Empty/Empty'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_EMAIL_TEMPLATES,
  DELETE_EMAIL_TEMPLATE,
} from 'src/graphql/emailTemplates.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import { buildFullName } from 'src/lib/formatters'

import CreateTemplateModal from './components/CreateTemplateModal'

const onSuccess = () => {
  toast('Your email template was deleted successfully', 'success')
}

const EmailTemplateTab = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [editing, setEditing] = useState(false)
  const [createModalOpened, createModalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const defaultResultsPerPage = 20

  const onErrorCallback = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  //QUERIES:
  const {
    data: templatesList,
    loading: templatesLoading,
    refetch,
  } = useQuery(
    GET_EMAIL_TEMPLATES,
    {
      variables: {
        pageInfo: {
          limit: defaultResultsPerPage,
          offset: 0,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      },
      onError: onErrorCallback,
    }
  )

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({
        pageInfo: {
          limit,
          offset,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      })
    },
    [refetch, debouncedSearchTerm]
  )

  //MUTATIONS:
  const [deleteTemplate] = useMutation(DELETE_EMAIL_TEMPLATE, {
    refetchQueries: [GET_EMAIL_TEMPLATES],
    onCompleted: () => {
      onSuccess()
    },
    onError: onErrorCallback,
  })

  //HANDLERS:
  const onEdit = (element) => {
    setSelectedTemplate(element)
    setEditing(true)
    createModalHandlers.open()
  }

  const onCreate = () => {
    setSelectedTemplate(undefined)
    setEditing(false)
    createModalHandlers.open()
  }

  const onDelete = () => {
    deleteTemplate({
      variables: {
        emailTemplateId: selectedTemplate.emailTemplateId,
      },
    })
  }

  useEffect(() => {
    if (!createModalOpened) {
      setSelectedTemplate(null)
    }
  }, [createModalOpened])

  const templates = templatesList?.emailTemplates?.items ?? []
  const totalCount = templatesList?.emailTemplates?.totalCount ?? 0

  const columns = useMemo(
    () => [
      {
        key: 'emailTemplateId',
        label: 'ID',
        width: 6,
        render: (template) => (
          <Tooltip label={template.emailTemplateId} withArrow>
            <ActionIcon
              variant="subtle"
              size={14}
              color="gray"
              onClick={(e) => {
                e.stopPropagation()
                navigator?.clipboard?.writeText?.(template.emailTemplateId)
              }}
            >
              <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        key: 'title',
        label: 'Template Name',
        width: 25,
        render: (template) => template.title,
      },
      {
        key: 'subject',
        label: 'Subject',
        width: 25,
        render: (template) => template.subject,
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 10,
        render: (template) =>
          template.createdBy ? (
            <Tooltip label={buildFullName(template.createdBy)} withArrow>
              <Avatar color="blue" size="sm" radius="sm">
                {template.createdBy.firstName?.charAt(0)}
                {template.createdBy.lastName?.charAt(0)}
              </Avatar>
            </Tooltip>
          ) : (
            'N/A'
          ),
      },
      {
        key: 'createdAt',
        label: 'Created Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.createdAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.createdAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'updatedAt',
        label: 'Updated Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.updatedAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.updatedAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 10,
        render: (template) => (
          <div className="flex flex-row justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(template)
                  }}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedTemplate(template)
                    deleteModalHandlers.open()
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [deleteModalHandlers, onEdit]
  )

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col ">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Email Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{totalCount}</span>
            <span> email templates</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton variant="primary" onClick={() => onCreate()}>
            Create Template
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={templates}
          enableColumnResize={false}
          enablePagination
          loading={templatesLoading}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          onRowClick={(_idx, row) => onEdit(row)}
          defaultResultsPerPage={defaultResultsPerPage}
          emptyStateMessage={
            <Empty title="No templates found" subtitle="Add one to get started" />
          }
        />
      </div>
      <CreateTemplateModal
        emailTemplate={selectedTemplate}
        edit={editing}
        isOpen={createModalOpened}
        onClose={() => createModalHandlers.close()}
        refetchQueries={[GET_EMAIL_TEMPLATES]}
      />
      <DeleteModal
        title={`Are you sure you want to delete the template ${selectedTemplate?.title}?`}
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default EmailTemplateTab
